// Bandeja de entregas: la cola a la izquierda y la revisión a la derecha (en celular, una
// debajo de la otra). La usa el tutor en /entregas y el admin en la ficha del proyecto
// (`admin` muestra todas las del proyecto, incluidas las automáticas).
// Al abrir una entrega pendiente queda «en revisión» a nombre de quien la abrió, para que
// otro tutor no la califique a la vez.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { entregasService, descargarEntrega } from '../../services/api';
import { fmtFechaHoraBogota, toDateUTC } from '../../utils/fecha';
import { fmtLimite } from './MiEntrega';

const ESTADOS = {
  pendiente: { txt: 'Por revisar', color: '#b7791f', fondo: '#fdf3e1' },
  aprobada: { txt: 'Aprobada', color: '#1f8a5b', fondo: '#e3f5ec' },
  automatica: { txt: 'Recibida (automática)', color: '#1f8a5b', fondo: '#e3f5ec' },
  devuelta: { txt: 'Devuelta', color: '#c2412d', fondo: '#fbe9e5' },
};
const FILTROS = [
  { v: 'pendiente', t: 'Por revisar' },
  { v: 'devuelta', t: 'Devueltas' },
  { v: 'aprobada', t: 'Aprobadas' },
  { v: 'automatica', t: 'Automáticas' },
  { v: '', t: 'Todas' },
];

// «hace 3 h», «hace 2 días»: lo que lleva esperando.
const espera = (f) => {
  const d = toDateUTC(f);
  if (!d) return '';
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const dias = Math.round(h / 24);
  return `hace ${dias} día${dias === 1 ? '' : 's'}`;
};
// De 0 (recién llegada) a 1 (una semana o más esperando).
const urgencia = (f) => {
  const d = toDateUTC(f);
  return d ? Math.min(1, (Date.now() - d.getTime()) / (7 * 864e5)) : 0;
};
const tamano = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const BandejaEntregas = ({ admin = false, proyectoId = null, acento = 'var(--project-primary, #0f766e)', estadoInicial = 'pendiente' }) => {
  const [estado, setEstado] = useState(estadoInicial);
  const [curso, setCurso] = useState('');
  const [q, setQ] = useState('');
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [selId, setSelId] = useState(null);
  const detalleRef = useRef(null);

  const cargar = useCallback(async () => {
    setError('');
    try {
      const r = await entregasService.lista({ estado, ...(proyectoId ? { proyecto_id: proyectoId } : {}) });
      setLista(r.data?.entregas || []);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Tu cuenta no está habilitada como tutor.' : 'No se pudieron cargar las entregas.');
      setLista([]);
    }
  }, [estado, proyectoId]);
  useEffect(() => { setLista(null); cargar(); }, [cargar]);

  const cursos = useMemo(() => [...new Set((lista || []).map(e => e.curso))].sort(), [lista]);
  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (lista || []).filter(e => (!curso || e.curso === curso)
      && (!t || `${e.alumno.nombre} ${e.alumno.email}`.toLowerCase().includes(t)));
  }, [lista, curso, q]);
  const sel = visibles.find(e => e.id === selId) || null;

  // Admin: la lista filtrada a un CSV que abre Excel (punto y coma y BOM para las tildes).
  const exportar = () => {
    const cols = [['Alumno', e => e.alumno.nombre], ['Correo', e => e.alumno.email], ['Curso', e => e.curso], ['Módulo', e => e.modulo],
      ['Lección', e => e.leccion], ['Intento', e => e.intento], ['Enviada', e => fmtFechaHoraBogota(e.enviado_at)],
      ['Estado', e => (ESTADOS[e.estado] || {}).txt || e.estado], ['Nota', e => (e.nota ?? '')], ['Revisó', e => e.revisor || ''],
      ['Revisada', e => (e.revisado_at ? fmtFechaHoraBogota(e.revisado_at) : '')], ['Archivos', e => e.archivos.map(a => a.nombre).join(', ')],
      ['Comentario del alumno', e => e.comentario_alumno || ''], ['Comentario del tutor', e => e.comentario_tutor || '']];
    const celda = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.map(c => celda(c[0])).join(';'), ...visibles.map(e => cols.map(c => celda(c[1](e))).join(';'))].join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `entregas-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const abrir = async (e) => {
    setSelId(e.id);
    if (window.matchMedia('(max-width: 900px)').matches) setTimeout(() => detalleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    // Pendiente y libre: queda en revisión a mi nombre.
    if (e.estado === 'pendiente' && !e.tomada) {
      try { await entregasService.tomar(e.id); await cargar(); } catch (_) { /* si otro la tomó, la lista lo mostrará */ }
    }
  };

  const tras = async () => { setSelId(null); await cargar(); window.dispatchEvent(new Event('entregas:cambio')); };

  return (
    <Mesa $acento={acento}>
      <Barra>
        <div className="estados" role="tablist" aria-label="Estado de las entregas">
          {FILTROS.map(f => (
            <button key={f.v} type="button" role="tab" aria-selected={estado === f.v} className={estado === f.v ? 'on' : ''}
              onClick={() => { setEstado(f.v); setSelId(null); }}>{f.t}</button>
          ))}
        </div>
        <div className="buscar">
          <select value={curso} onChange={e => setCurso(e.target.value)} aria-label="Curso">
            <option value="">Todos los cursos</option>
            {cursos.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="search" placeholder="Buscar alumno o correo" value={q} onChange={e => setQ(e.target.value)} />
          {admin && <button type="button" className="exportar" onClick={exportar} disabled={!visibles.length}>Exportar a Excel</button>}
        </div>
      </Barra>

      <Panel>
        <Cola aria-label="Entregas">
          {lista === null && <p className="vacio">Cargando…</p>}
          {error && <p className="vacio">{error}</p>}
          {lista && !error && visibles.length === 0 && (
            <p className="vacio">{estado === 'pendiente' ? 'No hay entregas por revisar. Cuando un alumno envíe una, aparecerá aquí.' : 'No hay entregas con este filtro.'}</p>
          )}
          {visibles.map(e => {
            const est = ESTADOS[e.estado] || ESTADOS.pendiente;
            return (
              <Fila key={e.id} type="button" onClick={() => abrir(e)} aria-current={e.id === selId ? 'true' : undefined}
                $on={e.id === selId} $u={e.estado === 'pendiente' ? urgencia(e.enviado_at) : null}>
                <span className="quien">{e.alumno.nombre}</span>
                <span className="cuando">{e.estado === 'pendiente' ? espera(e.enviado_at) : fmtFechaHoraBogota(e.revisado_at || e.enviado_at, { hour: undefined, minute: undefined })}</span>
                <span className="que">{e.leccion}</span>
                <span className="donde">{e.curso}{e.intento > 1 ? ` · intento ${e.intento}` : ''}{e.tarde ? ' · tarde' : ''}</span>
                {e.estado !== 'pendiente' && <Chip style={{ color: est.color, background: est.fondo }}>{est.txt}{e.nota !== null ? ` · ${e.nota}` : ''}</Chip>}
                {e.estado === 'pendiente' && e.tomada && <span className="tomada">{e.tomada.mia ? 'La estás revisando' : `La revisa ${e.tomada.nombre}`}</span>}
              </Fila>
            );
          })}
        </Cola>

        <Detalle ref={detalleRef}>
          {!sel ? (
            <p className="vacio">Elige una entrega de la lista para revisarla.</p>
          ) : (
            <Revision key={sel.id} e={sel} admin={admin} onHecho={tras} onSoltar={async () => { await entregasService.tomar(sel.id, true); setSelId(null); await cargar(); }} />
          )}
        </Detalle>
      </Panel>
    </Mesa>
  );
};

const Revision = ({ e, admin, onHecho, onSoltar }) => {
  // Si ya tenía nota o comentario (reabierta, o se va a comentar), se parte de ahí.
  const [nota, setNota] = useState(e.nota ?? '');
  const [comentario, setComentario] = useState(e.comentario_tutor || '');
  const [archivos, setArchivos] = useState([]);
  const [enviando, setEnviando] = useState('');
  const [error, setError] = useState('');
  const [comentando, setComentando] = useState(false);
  // Rúbrica: un puntaje por criterio; la nota es la suma.
  const criterios = e.rubrica_config || [];
  const [puntos, setPuntos] = useState(() => criterios.map((_, i) => e.rubrica?.[i]?.puntos ?? ''));
  const suma = puntos.reduce((t, v) => t + (Number(String(v).replace(',', '.')) || 0), 0);
  const maximo = criterios.reduce((t, r) => t + Number(r.max || 0), 0);
  const camposNota = (deshabilitado) => (criterios.length ? (
    <div className="rubrica">
      <span>Rúbrica{e.nota_minima !== null ? ` (mínima ${e.nota_minima})` : ''}</span>
      {criterios.map((r, i) => (
        <label key={i} className="criterio">
          <span>{r.criterio}</span>
          <input type="number" min={0} max={r.max} step="0.1" inputMode="decimal" value={puntos[i]} disabled={deshabilitado}
            onChange={ev => setPuntos(puntos.map((v, j) => (j === i ? ev.target.value : v)))} aria-label={`${r.criterio}, de 0 a ${r.max}`} />
          <small>/ {r.max}</small>
        </label>
      ))}
      <p className="total">Nota: <b>{Math.round(suma * 100) / 100}</b> de {maximo}</p>
    </div>
  ) : (
    <label className="nota">
      <span>Nota{e.nota_minima !== null ? ` (mínima ${e.nota_minima})` : ' (opcional)'}</span>
      <input type="number" step="0.1" inputMode="decimal" value={nota} onChange={ev => setNota(ev.target.value)} disabled={deshabilitado} />
    </label>
  ));
  const [confirmarReabrir, setConfirmarReabrir] = useState(false);

  const reabrir = async () => {
    setError(''); setEnviando('reabrir');
    try { await entregasService.reabrir(e.id); await onHecho(); }
    catch (err) { setError(err.response?.data?.message || 'No se pudo reabrir.'); setEnviando(''); }
  };
  const est = ESTADOS[e.estado] || ESTADOS.pendiente;
  const bloqueada = e.estado === 'pendiente' && e.tomada && !e.tomada.mia && !admin;

  const calificar = async (decision) => {
    setError('');
    if (decision === 'devolver' && !comentario.trim()) { setError('Escribe al alumno qué debe corregir.'); return; }
    setEnviando(decision);
    try {
      await entregasService.calificar(e.id, { decision, nota, comentario, archivos, rubrica: criterios.length && puntos.some(v => v !== '') ? puntos : null });
      await onHecho();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo guardar la revisión.');
      setEnviando('');
    }
  };

  return (
    <>
      <header className="cab">
        <h2>{e.alumno.nombre}</h2>
        <a href={`mailto:${e.alumno.email}`}>{e.alumno.email}</a>
        <p className="ruta">{e.curso} › {e.modulo !== e.leccion ? `${e.modulo} › ` : ''}{e.leccion}</p>
        <p className="meta">
          Enviada el {fmtFechaHoraBogota(e.enviado_at)}{e.intento > 1 ? `, intento ${e.intento}` : ''}
          {e.tarde && <Chip style={{ color: '#c2412d', background: '#fbe9e5', marginLeft: 8 }}>Tarde</Chip>}
          {e.fecha_limite && <span className="limite">Fecha límite: {fmtLimite(e.fecha_limite)}</span>}
        </p>
      </header>

      <Bloque>
        <h3>Archivos del alumno</h3>
        <div className="archivos">
          {e.archivos.map(a => (
            <button key={a.i} type="button" onClick={() => descargarEntrega(e.id, a.i, a.nombre)}>
              <b>{a.nombre}</b><span>{tamano(a.tamano)} · descargar</span>
            </button>
          ))}
        </div>
        {e.comentario_alumno && <blockquote>{e.comentario_alumno}</blockquote>}
      </Bloque>

      {e.estado === 'pendiente' ? (
        <Bloque as="form" onSubmit={ev => ev.preventDefault()}>
          <h3>Tu revisión</h3>
          {bloqueada && <p className="aviso">La está revisando {e.tomada.nombre}. Podrás calificarla si la suelta o pasan 3 días sin respuesta.</p>}
          <div className="campos">
            {camposNota(bloqueada)}
            <label>
              <span>Comentario para el alumno</span>
              <textarea rows={4} value={comentario} onChange={ev => setComentario(ev.target.value)} disabled={bloqueada}
                placeholder="Qué hizo bien y, si la devuelves, qué debe corregir." />
            </label>
            <label className="adjuntar">
              <span>Adjuntar archivo corregido (opcional)</span>
              <input type="file" multiple disabled={bloqueada} onChange={ev => setArchivos(Array.from(ev.target.files || []))} />
            </label>
          </div>
          {error && <p className="error" role="alert">{error}</p>}
          <div className="acciones">
            <Boton type="button" $tipo="aprobar" disabled={bloqueada || !!enviando} onClick={() => calificar('aprobar')}>
              {enviando === 'aprobar' ? 'Aprobando…' : 'Aprobar'}
            </Boton>
            <Boton type="button" $tipo="devolver" disabled={bloqueada || !!enviando} onClick={() => calificar('devolver')}>
              {enviando === 'devolver' ? 'Devolviendo…' : 'Devolver para corregir'}
            </Boton>
            {e.tomada?.mia && <button type="button" className="soltar" onClick={onSoltar}>Soltar para otro tutor</button>}
          </div>
        </Bloque>
      ) : (
        <Bloque>
          <h3>Resultado</h3>
          <Chip style={{ color: est.color, background: est.fondo }}>{est.txt}{e.nota !== null ? ` · nota ${e.nota}` : ''}</Chip>
          {e.rubrica?.length > 0 && (
            <table className="desglose"><tbody>
              {e.rubrica.map((r, i) => <tr key={i}><td>{r.criterio}</td><td>{r.puntos} / {r.max}</td></tr>)}
            </tbody></table>
          )}
          {e.comentario_tutor && <blockquote>{e.comentario_tutor}</blockquote>}
          {e.archivos_tutor.length > 0 && (
            <div className="archivos">
              {e.archivos_tutor.map(a => (
                <button key={a.i} type="button" onClick={() => descargarEntrega(e.id, a.i, a.nombre, true)}><b>{a.nombre}</b><span>archivo del tutor</span></button>
              ))}
            </div>
          )}
          <p className="meta">
            {e.estado === 'automatica' && !e.revisor ? 'Se aprobó sola al enviarla (la lección tiene aprobación automática).'
              : `${e.estado === 'automatica' ? 'Comentada' : 'Revisada'} por ${e.revisor || 'el tutor'} el ${fmtFechaHoraBogota(e.revisado_at)}`}
          </p>

          {comentando ? (
            <div className="campos" style={{ marginTop: '1rem' }}>
              {camposNota(false)}
              <label>
                <span>Comentario para el alumno</span>
                <textarea rows={3} value={comentario} onChange={ev => setComentario(ev.target.value)} />
              </label>
              <label className="adjuntar">
                <span>Adjuntar archivo (opcional)</span>
                <input type="file" multiple onChange={ev => setArchivos(Array.from(ev.target.files || []))} />
              </label>
            </div>
          ) : null}
          {error && <p className="error" role="alert">{error}</p>}
          <div className="acciones">
            {['aprobada', 'automatica'].includes(e.estado) && (comentando ? (
              <>
                <Boton type="button" $tipo="aprobar" disabled={!!enviando} onClick={() => calificar('comentar')}>{enviando === 'comentar' ? 'Guardando…' : 'Guardar y avisar al alumno'}</Boton>
                <button type="button" className="soltar" onClick={() => setComentando(false)}>Cancelar</button>
              </>
            ) : (
              <button type="button" className="secundario" onClick={() => setComentando(true)}>{e.comentario_tutor || e.nota !== null ? 'Cambiar nota o comentario' : 'Agregar nota o comentario'}</button>
            ))}
            {['aprobada', 'devuelta'].includes(e.estado) && !comentando && (confirmarReabrir ? (
              <span className="confirmar">
                {e.estado === 'aprobada' ? 'La lección del alumno vuelve a quedar sin completar hasta que la califiques de nuevo. ' : ''}
                <Boton type="button" $tipo="devolver" disabled={!!enviando} onClick={reabrir}>{enviando === 'reabrir' ? 'Reabriendo…' : 'Sí, reabrir'}</Boton>
                <button type="button" className="soltar" onClick={() => setConfirmarReabrir(false)}>Cancelar</button>
              </span>
            ) : (
              <button type="button" className="secundario" onClick={() => setConfirmarReabrir(true)}>Reabrir revisión</button>
            ))}
          </div>
        </Bloque>
      )}
    </>
  );
};

export default BandejaEntregas;

/* ---------- estilos ---------- */
const tinta = '#1f2a37';
const suave = '#5b6675';
const linea = '#e3e8ee';

const Mesa = styled.div`
  --acento: ${p => p.$acento};
  color: ${tinta};
  font-size: 15px;
  button, input, select, textarea { font: inherit; }
  :focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  .vacio { margin: 0; padding: 2.5rem 1.2rem; color: ${suave}; text-align: center; line-height: 1.5; }
`;
const Barra = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .8rem; margin-bottom: 1rem;
  .estados { display: flex; flex-wrap: wrap; gap: .3rem; }
  .estados button {
    border: 1px solid ${linea}; background: #fff; color: ${suave}; padding: .45rem .95rem; border-radius: 999px; cursor: pointer; font-weight: 600; font-size: .9rem;
  }
  .estados button.on { background: var(--acento); border-color: var(--acento); color: #fff; }
  .buscar { display: flex; flex-wrap: wrap; gap: .5rem; }
  select, input[type=search] { padding: .5rem .75rem; border: 1px solid ${linea}; border-radius: 8px; background: #fff; color: ${tinta}; min-width: 0; max-width: 100%; }
  input[type=search] { width: 220px; }
  .exportar { padding: .5rem .9rem; border: 1px solid var(--acento); border-radius: 8px; background: #fff; color: var(--acento); font-weight: 600; cursor: pointer; }
  .exportar:disabled { opacity: .5; cursor: default; }
  @media (max-width: 600px) { .buscar, .buscar select, .buscar input[type=search] { width: 100%; } }
`;
const Panel = styled.div`
  display: grid; grid-template-columns: minmax(280px, 380px) minmax(0, 1fr); gap: 1.2rem; align-items: start;
  @media (max-width: 900px) { grid-template-columns: minmax(0, 1fr); }
`;
const Cola = styled.div`
  background: #fff; border: 1px solid ${linea}; border-radius: 12px; overflow: hidden; max-height: 72vh; overflow-y: auto;
  @media (max-width: 900px) { max-height: none; }
`;
const Chip = styled.span`
  display: inline-block; padding: .18rem .65rem; border-radius: 999px; font-size: .78rem; font-weight: 700;
`;
const Fila = styled.button`
  display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: .15rem .8rem; width: 100%; text-align: left; cursor: pointer;
  padding: .85rem 1rem .85rem 1.15rem; border: 0; border-bottom: 1px solid ${linea}; background: #fff; position: relative;
  /* La barra lateral se calienta mientras más espera la entrega. */
  ${p => p.$u !== null && css`
    &::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
      background: hsl(${Math.round(150 - p.$u * 145)}, ${55 + Math.round(p.$u * 25)}%, ${45 - Math.round(p.$u * 5)}%); }
  `}
  ${p => p.$on && css`background: #f3f8f8; box-shadow: inset 0 0 0 2px var(--acento);`}
  &:hover { background: #f7f9fb; }
  .quien { font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cuando { color: ${suave}; font-size: .82rem; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .que { grid-column: 1 / -1; font-size: .9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .donde { grid-column: 1 / -1; color: ${suave}; font-size: .8rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tomada { grid-column: 1 / -1; color: #b7791f; font-size: .8rem; font-weight: 600; }
  > ${Chip} { grid-column: 1 / -1; justify-self: start; margin-top: .2rem; }
`;
const Detalle = styled.section`
  background: #fff; border: 1px solid ${linea}; border-radius: 12px; padding: 1.4rem clamp(1rem, 3vw, 1.8rem); min-height: 240px;
  scroll-margin-top: 90px;
  .cab h2 { margin: 0; font-size: 1.35rem; line-height: 1.2; }
  .cab a { color: var(--acento); font-size: .9rem; overflow-wrap: anywhere; }
  .ruta { margin: .6rem 0 0; font-weight: 600; line-height: 1.4; }
  .meta { margin: .3rem 0 0; color: ${suave}; font-size: .86rem; }
  .meta .limite { display: block; margin-top: .2rem; }
`;
const Bloque = styled.div`
  margin-top: 1.3rem; padding-top: 1.2rem; border-top: 1px solid ${linea};
  h3 { margin: 0 0 .7rem; font-size: 1rem; }
  .archivos { display: flex; flex-wrap: wrap; gap: .5rem; }
  .archivos button {
    display: flex; flex-direction: column; align-items: flex-start; gap: .1rem; max-width: 100%; text-align: left; cursor: pointer;
    padding: .6rem .85rem; border: 1px solid ${linea}; border-radius: 10px; background: #f9fbfc; color: ${tinta};
  }
  .archivos button:hover { border-color: var(--acento); }
  .archivos b { font-weight: 600; overflow-wrap: anywhere; }
  .archivos span { color: ${suave}; font-size: .8rem; }
  blockquote { margin: .8rem 0 0; padding: .6rem .9rem; border-left: 3px solid ${linea}; color: ${suave}; white-space: pre-line; line-height: 1.5; }
  .campos { display: grid; gap: .8rem; }
  label { display: grid; gap: .3rem; }
  label > span { font-size: .86rem; font-weight: 600; color: ${suave}; }
  .nota input { width: 120px; }
  .rubrica { display: grid; gap: .45rem; }
  .rubrica > span { font-size: .86rem; font-weight: 600; color: ${suave}; }
  .rubrica .criterio { display: grid; grid-template-columns: minmax(0, 1fr) 90px auto; align-items: center; gap: .6rem; }
  .rubrica .criterio > span { font-weight: 400; color: ${tinta}; font-size: .92rem; }
  .rubrica .criterio input { width: 100%; }
  .rubrica small { color: ${suave}; }
  .rubrica .total { margin: .2rem 0 0; color: ${suave}; }
  .rubrica .total b { color: ${tinta}; font-size: 1.05rem; }
  .desglose { margin-top: .7rem; border-collapse: collapse; font-size: .9rem; }
  .desglose td { padding: .2rem 1.2rem .2rem 0; }
  .desglose td:last-child { font-variant-numeric: tabular-nums; font-weight: 600; }
  input[type=number], textarea { padding: .55rem .75rem; border: 1px solid ${linea}; border-radius: 8px; color: ${tinta}; background: #fff; box-sizing: border-box; }
  textarea { width: 100%; resize: vertical; line-height: 1.5; }
  .adjuntar input { font-size: .86rem; max-width: 100%; }
  .aviso { margin: 0 0 .8rem; padding: .6rem .8rem; border-radius: 8px; background: #fdf3e1; color: #8a5a12; font-size: .9rem; }
  .error { margin: .7rem 0 0; color: #c2412d; font-size: .9rem; }
  .acciones { display: flex; flex-wrap: wrap; align-items: center; gap: .6rem; margin-top: 1rem; }
  .soltar { border: 0; background: none; color: ${suave}; text-decoration: underline; cursor: pointer; padding: .4rem; }
  .secundario { padding: .55rem 1rem; border: 1px solid ${linea}; border-radius: 8px; background: #fff; color: ${tinta}; font-weight: 600; cursor: pointer; }
  .secundario:hover { border-color: var(--acento); }
  .confirmar { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; color: ${suave}; font-size: .9rem; line-height: 1.4; }
`;
const Boton = styled.button`
  padding: .65rem 1.3rem; border-radius: 8px; font-weight: 700; cursor: pointer;
  ${p => p.$tipo === 'aprobar'
    ? css`border: 0; background: #1f8a5b; color: #fff; &:hover:not(:disabled) { background: #187049; }`
    : css`border: 1.5px solid #c2412d; background: #fff; color: #c2412d; &:hover:not(:disabled) { background: #fbe9e5; }`}
  &:disabled { opacity: .5; cursor: default; }
`;
